<template>
  <BlockFieldset class="project-customer-installation-address">
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <b-form-group
          horizontal
          :label="$t('installationAddressState')"
          label-for="installationAddressState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <SelectButton
            :disabled="isBlockReadOnly"
            v-model="project.installationAddressState"
            :options="getEnumValues('YesNo')"
          >
            <template #option="props">
              {{ $t(props.option) }}
            </template>
          </SelectButton>
        </b-form-group>
        <div v-if="project.installationAddressState === 'YES'">
          <b-form-group
            horizontal
            :label="$t('street')"
            label-for="installationStreet"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input
              v-model="project.installationAddress.street"
              type="text"
              :placeholder="$t('street')"
            />
          </b-form-group>
          <b-form-group
            horizontal
            :label="$t('streetNumber')"
            label-for="installationStreetNumber"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input
              v-model="project.installationAddress.streetNumber"
              type="text"
              :placeholder="$t('streetNumber')"
            />
          </b-form-group>
          <b-form-group
            horizontal
            :label="$t('streetNumberSuffix')"
            label-for="installationStreetNumberSuffix"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input
              v-model="project.installationAddress.streetNumberSuffix"
              type="text"
              :placeholder="$t('streetNumberSuffix')"
            />
          </b-form-group>
          <b-form-group
            horizontal
            :label="$t('zip')"
            label-for="installationZip"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input
              v-model="project.installationAddress.zip"
              type="text"
              :placeholder="$t('zip')"
            />
          </b-form-group>
          <b-form-group
            horizontal
            :label="$t('city')"
            label-for="installationCity"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input
              v-model="project.installationAddress.city"
              type="text"
              :placeholder="$t('city')"
            />
          </b-form-group>
        </div>
      </b-col>
      <b-col lg="6" md="6" sm="12">
        <b-form-group
          v-if="project.installationAddressState === 'YES'"
          horizontal
          :label="$t('cityPart')"
          label-for="installationCityPart"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="project.installationAddress.cityPart"
            type="text"
            :placeholder="$t('cityPart')"
          />
        </b-form-group>
        <b-form-group
          horizontal
          :label="$t('longitude')"
          label-for="longitude"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          :invalid-feedback="$t('customer_installation_address_decimal_invalid')"
          :state="coordinateState('longitude')"
        >
          <b-form-input
            v-model="project.customer.longitude"
            type="text"
            :placeholder="$t('customer_installation_address_decimal_placeholder')"
            :state="coordinateState('longitude')"
          />
        </b-form-group>
        <b-form-group
          horizontal
          :label="$t('latitude')"
          label-for="latitude"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          :invalid-feedback="$t('customer_installation_address_decimal_invalid')"
          :state="coordinateState('latitude')"
        >
          <b-form-input
            v-model="project.customer.latitude"
            type="text"
            :placeholder="$t('customer_installation_address_decimal_placeholder')"
            :state="coordinateState('latitude')"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <div v-if="hasSaveButton" class="text-right mt-2">
      <b-button variant="bg" :disabled="isSaving" @click="save">
        {{ $t('saveNamed', { name: $t('project_customer_installation_address') }) }}
      </b-button>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SelectButton from 'primevue/selectbutton';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import {
  EMPTY_INSTALLATION_ADDRESS,
  isValidInstallationAddressDecimal,
  toProjectCustomerInstallationAddressInput,
} from './projectCustomerInstallationAddressInput';

const NEW_PROJECT_NUMBER = '_new';

export default {
  name: 'ProjectCustomerInstallationAddress',
  components: { BlockFieldset, SelectButton },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  computed: {
    ...mapGetters(['getEnumValues']),
    blockInput() {
      return toProjectCustomerInstallationAddressInput(this.project);
    },
    hasSaveButton() {
      return !this.isBlockReadOnly && this.project.number !== NEW_PROJECT_NUMBER;
    },
  },
  watch: {
    project: {
      handler: 'initBlockData',
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['updateProjectCustomerInstallationAddress']),
    saveBlockInput(input) {
      return this.updateProjectCustomerInstallationAddress({
        projectNumber: this.project.number,
        input,
      });
    },
    initBlockData() {
      if (!this.project.installationAddress) {
        this.$set(this.project, 'installationAddress', { ...EMPTY_INSTALLATION_ADDRESS });
      }
      if (!this.project.installationAddressState) {
        this.$set(this.project, 'installationAddressState', 'NO');
      }
    },
    coordinateState(field) {
      const value = this.project.customer[field];
      if (isValidInstallationAddressDecimal(value)) {
        return null;
      }

      return false;
    },
  },
};
</script>
